import { Button, styled, Typography } from '@mui/material';
import Dropzone, { UploadedFile } from 'components/Dropzone';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import {
  PreferredReturnAccrualMethod,
  PreferredReturnAccrualPeriod,
  PreferredReturnBasis,
  PreferredReturnCalculationEnd,
  PreferredReturnCompoundingPeriod,
  WaterfallStructure,
} from 'further-types/lp/carry-settings';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column } from 'further-ui/components/Layout';
import {
  CalendarDay,
  convertFileToBase64Content,
  FileType,
} from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import React, { useState } from 'react';
import downloadXlxs from 'utils/downloadXlxs';
import {
  CapitalActionFormState,
  UpdateCapitalActionFormValues,
} from '../../../hooks/useCapitalActionForm';

const TextButton = styled(Button)({
  padding: 0,
  margin: 0,
  minWidth: 'auto',
  width: 'auto',
});

export type FundingsRemainingToDistribute = {
  commitmentId: string;
  carriedInterestFeeTemplateId: string | null;
  investorLabel: string;
  isCarryPartnerCommitment: boolean;
  waterfall: {
    totalCommitment: number;
    paidInCapital: number;
    grossProceeds: number;
    preferredReturnAccrued: {
      accruedPreferredReturn: number;
      calculationDetails: Array<{
        date: CalendarDay;
        dailyInterest: number;
        totalInterest: number;
        accruedPreferredReturn: number;
      }>;
    };
    catchUpAccrued: number;
    waterfallElements: Array<{
      element: WaterfallStructure;
      value: number;
    }>;
    capitalSplit: {
      carryPartner: {
        excessProceeds: number;
        remainingToDistribute: number;
        totalDistributable: number;
        distributedToDate: number;
      };
      limitedPartner: {
        excessProceeds: number;
        remainingToDistribute: number;
        totalDistributable: number;
        distributedToDate: number;
        dpi: number;
      };
    };
    carriedInterestFeeTemplate: {
      carriedInterestRates: Array<{
        rate: number;
        dpiThreshold: number | undefined | null;
      }>;
      carriedInterestPartnerCatchupBetweenRateThresholds: boolean;
      preferredReturnRate: number;
      preferredReturnAccrualMethod: PreferredReturnAccrualMethod;
      preferredReturnAccrualPeriod: PreferredReturnAccrualPeriod;
      preferredReturnCompoundingPeriod?: PreferredReturnCompoundingPeriod | null;
      preferredReturnCalculationEnd: PreferredReturnCalculationEnd;
      preferredReturnBasis: PreferredReturnBasis;
      partnerCatchupSpeed: number;
    };
  };
  outsideOfCarryCalculation: {
    fundsFromIncomes: number;
    fundsFromFxGains: number;
    distributedToDate: number;
    actionHistory: Array<{
      actionRef: string;
      actionId: string;
      amountLcy?: number | null;
      date: CalendarDay;
    }>;
  };
  availableToDistributeSubjectToCarry: number;
  availableToDistributeOutsideOfCarry: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onUpload: (result: Array<FundingsRemainingToDistribute>) => void;
  vehicleId: string;
  date: CalendarDay;
};

const UploadManualOutputsModal: React.FC<Props> = ({
  open,
  onClose,
  onUpload,
  vehicleId,
  date,
}) => {
  const {
    formMethods: { getValues },
  } = useFormWithStateContext<
    UpdateCapitalActionFormValues,
    CapitalActionFormState
  >();
  const entries = getValues('entries') ?? [];
  const [fileToUpload, setFileToUpload] = useState<{
    filePath: string;
    name: string;
    url: string;
    fileContent: string;
    type: string;
  } | null>(null);

  const { error } = useNotification();

  const validateManualOutputsFileMutation =
    api.capitalAction.validateManualOutputsFile.useMutation(
      useHandleTrpcMutation<Array<FundingsRemainingToDistribute>>()({
        successMessage: 'File validated successfully',
        onSuccess: (result) => {
          onUpload(result);
          setFileToUpload(null);
          onClose();
        },
      }),
    );

  const uploadManualOutputs =
    api.capitalAction.exportManualOutputsFile.useMutation({
      onSuccess: (data) => {
        downloadXlxs(data.file, 'Manual outputs');
      },
    });

  const handleFileUpload = async (files: Array<UploadedFile>) => {
    if (!files?.[0]) return;

    try {
      const file = files[0];

      const base64Content = await convertFileToBase64Content(file);

      setFileToUpload({
        filePath: file.name,
        name: file.name,
        url: file.preview,
        fileContent: base64Content,
        type: file.type,
      });
    } catch (_err) {
      error('Failed to process file. Please try again.');
    }
  };

  const handleDelete = () => {
    setFileToUpload(null);
  };

  const handleConfirm = async () => {
    if (fileToUpload) {
      await validateManualOutputsFileMutation.mutateAsync({
        vehicleId,
        fileContent: fileToUpload.fileContent,
        fileName: fileToUpload.name,
        fileMimeType: fileToUpload.type,
        date,
        entries,
      });
    }
  };

  return (
    <ConfirmDialog
      title="Upload Manual Outputs"
      open={open}
      maxWidth="sm"
      onClose={() => {
        setFileToUpload(null);
        onClose();
      }}
      loading={validateManualOutputsFileMutation.isPending}
      actions={{
        confirm: {
          onClick: fileToUpload ? handleConfirm : () => {},
          label: 'Upload',
        },
      }}
      content={
        <Column spacing="sm">
          <Column spacing="sm">
            <Typography variant="body2">
              Please upload{' '}
              <TextButton
                variant="text"
                onClick={() =>
                  uploadManualOutputs.mutate({
                    vehicleId,
                    date,
                    entries,
                  })
                }
              >
                this
              </TextButton>{' '}
              Excel file (.xlsx) containing the manual outputs data.
            </Typography>
            <Typography variant="body2">
              The file will be validated before processing.
            </Typography>
          </Column>

          <Dropzone
            name="manualOutputsFile"
            accept={{
              [FileType.ExcelAlt]: ['.xlsx'],
            }}
            maxFiles={1}
            onDropFile={handleFileUpload}
            files={
              fileToUpload
                ? [
                    {
                      preview: fileToUpload.url,
                      fileName: fileToUpload.name,
                    },
                  ]
                : []
            }
            onRemove={handleDelete}
            disabled={validateManualOutputsFileMutation.isPending}
          />
        </Column>
      }
    />
  );
};

export default UploadManualOutputsModal;
